import { useEffect, useState, Fragment, useMemo, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useHistory } from 'react-router-dom'
import LinkCard from './LinkCard'
import S3Image from '../common/S3Image'
import Breadcrumb from '../common/Breadcrumb'
import LoadingSpinner from '../common/LoadingSpinner'
import EmptyPageResults from '../common/EmptyPageResults'
import VideoModal from '../common/VideoModal'
import SimpleButton from '../common/SimpleButton'
import { useWindowSize } from '../../libs/hooks'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { getDashboardHowToVideos } from '../../utils/functions'
import { currentDay } from '../../utils/filters/date'
import { Pages } from '../../utils/pages'
import { hasCorporateAdminModulesPermission, hasEndUserSiteAccess } from '../../utils/permissionValidation'
import { getVideosList } from '../../utils/requests/videosAPI'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { COLTENE_BRANCH_UUID } from '../../actions/root'
import { loadCorporateBranches } from '../../actions/corporate'
import { CompaniesIcon, VideoPlayIcon } from '../../icons'

const corporateDashboardVideosCache = new Map()
const corporateDashboardVideosInFlight = new Map()

function CorporateDashboard(props) {
  const isMobile = useWindowSize()[0] <= 768
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const [_, setColteneBranchId] = useLocalStorage(COLTENE_BRANCH_UUID)
  const { linkCards } = props
  const [userPermissions, setUserPermissions] = useState([])
  const [videos, setVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showSpinner, setShowSpinner] = useState(null)
  const [videosLoading, setVideosLoading] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const showVideos = hasCorporateAdminModulesPermission(props.userPermissions) && hasEndUserSiteAccess(props.userPermissions)

  useEffect(() => {
    if (!props.companyId) {
      return
    }

    const queryParams = {
      offset: 0,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }
    props.actions.loadCorporateBranches(props.companyId, queryParams, true).finally(() => {
      setIsFirstLoad(false)
    })
  }, [props.companyId])

  useEffect(() => {
    const permissionArr = props.userPermissions ? (Array.isArray(props.userPermissions) ? props.userPermissions : Object.values(props.userPermissions)) : []
    setUserPermissions(permissionArr)
  }, [props.userPermissions])

  useEffect(() => {
    if (!showVideos) {
      setVideos([])
      return
    }

    const getVideos = async () => {
      const userKey = props.authedUser?.cognitoSub || 'anonymous'
      setVideosLoading(true)

      try {
        if (corporateDashboardVideosCache.has(userKey)) {
          setVideos(corporateDashboardVideosCache.get(userKey) || [])
          return
        }

        let request = corporateDashboardVideosInFlight.get(userKey)
        if (!request) {
          request = getVideosList()
            .then((videosData) => {
              const list = Array.isArray(videosData?.data) ? videosData.data : []
              corporateDashboardVideosCache.set(userKey, list)
              return list
            })
            .finally(() => {
              corporateDashboardVideosInFlight.delete(userKey)
            })
          corporateDashboardVideosInFlight.set(userKey, request)
        }

        const videosData = await request
        setVideos(videosData || [])
      } finally {
        setVideosLoading(false)
      }
    }

    getVideos()
  }, [props.authedUser?.cognitoSub, showVideos])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useLayoutEffect(() => {
    setSelectedLanguage(i18n.language?.split('-')[0]?.toUpperCase() || 'EN')
  }, [i18n.language])

  const dashboardVideos = useMemo(() => {
    return getDashboardHowToVideos(videos, selectedLanguage)
  }, [videos, selectedLanguage])

  const handleViewMyBranchesClick = () => {
    history.push({ pathname: Pages.myBranches.route })
  }

  const handleSelectBranch = (branch) => {
    setColteneBranchId(branch.branchUuid, false)
    window.location.replace('/')
  }

  const openVideoModal = (item) => {
    setSelectedVideo(item)
  }

  const closeVideoModal = () => {
    setSelectedVideo(null)
  }

  return (
    <div className="dentist-dashboard-wrapper">
      <Breadcrumb path={window.location.pathname} />
      <div className={`dashboard-title d-flex ${isMobile ? 'flex-column' : 'flex-row'} flex-justify-between`}>
        <h1>{t('dashboard.welcome', { firstName: props.authedUser?.firstname, lastName: props.authedUser?.lastname })}</h1>
        <div>{currentDay()}</div>
      </div>
      <div className={`dashboard-content d-flex ${isMobile ? 'flex-column' : 'flex-row'} flex-justify-between`}>
        <div className={`links-wrapper d-flex ${isMobile ? 'with-order-2 flex-column' : 'flex-row'}`}>
          {linkCards.map((card, index) => (
            <Fragment key={index}>{userPermissions.includes(card.permission) && <LinkCard cardContent={card} />}</Fragment>
          ))}
        </div>
        <div className={`products-wrapper ${isMobile ? 'with-order-1' : ''}`}>
          <div className="products-title">{t('my_locations')}</div>
          <div className="products-list d-flex flex-column">
            {props.branches.slice(0, 10).map((branch, index) => (
              <div key={branch.branchUuid} className="branch-card" onClick={() => handleSelectBranch(branch)}>
                <S3Image imageKey={branch.imageUrl} fallbackIcon={<CompaniesIcon />} />
                <div className="data-wrapper">
                  <div className="title">{branch.branchName}</div>
                  <div className="devices-count">{`${branch.totalDevices} ${t('products_single_many')}`}</div>
                </div>
              </div>
            ))}
            {!showSpinner && !isFirstLoad && props.branches.length === 0 && <EmptyPageResults title={t('organization_context.no-branches-found')} />}
          </div>
          <div className="products-actions d-flex flex-justify-end">
            <SimpleButton className="submit-button" onClick={handleViewMyBranchesClick}>
              {t('view_all')}
            </SimpleButton>
          </div>
        </div>
      </div>
      {showVideos && dashboardVideos.length > 0 && (
        <div className="videos-content d-flex flex-column">
          <div className="section-title">{t('nav.videos')}</div>
          <div className="videos-wrapper">
            {dashboardVideos.map((video, index) => (
              <div
                key={index}
                className="video-item-wrapper"
                onClick={() => openVideoModal(video)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => e.key === keyboardEvents.ENTER && openVideoModal(video)}
              >
                <div className="thumbnail-wrapper">
                  <div className="thumbnail-icon">
                    <VideoPlayIcon />
                  </div>
                  <img src={video.thumbnail_url} alt="video" />
                </div>
                <p className="title">{video.title}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedVideo && <VideoModal title={selectedVideo.title} videoId={selectedVideo.video_id} onCloseVideo={closeVideoModal} />}
      {(showSpinner || videosLoading) && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function mapStateToProps({ corporate, authedUser, userCompany, userPermissions }) {
  return {
    branches: corporate?.branches || [],
    isLoading: corporate?.isLoading,
    companyId: userCompany?.companyId,
    authedUser,
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCorporateBranches,
      },
      dispatch,
    ),
  }
}

export default connect(mapStateToProps, dispatchToProps)(CorporateDashboard)

